import { type Dispatch, type SetStateAction, useState } from 'react';
import { BigNumber } from 'bignumber.js';
import { secondsInDay } from 'date-fns/constants';
import { BannerCard, APYText } from '@/shared/components/BannerCard';
import Button from '@/shared/components/flip-ui-kit/Button';
import { TABLE_POLL_INTERVAL } from '@/shared/constants';
import { type CacheValidatorFragment } from '@/shared/graphql/generated/cache/graphql';
import useAuction from '@/shared/hooks/useAuction';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useTracking from '@/shared/hooks/useTracking';
import type TokenAmount from '@/shared/utils/TokenAmount';
import { RegisterNodeModal } from './RegisterNodeModal';
import Dots from '../../../assets/svg/validator-dots.svg';
import { getApyValidators } from '../../../graphql/cache/validatorQueries';
import { StakeEvents, type StakeTrackEvents } from '../../../types/track';

// 1. 150, apy of the last one > 0 => show the APY of the last one
// 2. 150, apy of the last one != 0 => calculate the APY based on the bid amount of the validator (BID of one validator who has positive APY / bid of the last validator * APY of the validator with positive APY)
// 3. <150, calculate the APY based on 50K amount (BID of one validator who has positive APY / 50,000 * APY of the validator with positive APY)
const calculateValidatorApy = ({
  validators,
  authoritySetMaxCapacity,
  minValidatorStake,
  blockReward,
}: {
  validators: CacheValidatorFragment[];
  authoritySetMaxCapacity: number;
  minValidatorStake: TokenAmount;
  blockReward: TokenAmount;
}) => {
  const numberOfValidators = validators.length;

  if (numberOfValidators === 0) return 0;

  if (numberOfValidators === authoritySetMaxCapacity) {
    return validators.at(-1)!.apyBp! / 10_000;
  }

  const authorshipSlotsPerDay = secondsInDay / 6 / (numberOfValidators + 1);

  // calculate the apy based off the block reward and minimum balance
  return blockReward.value
    .times(authorshipSlotsPerDay)
    .times(365)
    .dividedBy(BigNumber.max(minValidatorStake.value, 10e18))
    .toNumber();
};

export const SetupValidatorBannerCard = ({
  idSs58,
  setSelectedTab,
}: {
  idSs58?: string;
  setSelectedTab: Dispatch<SetStateAction<'overview' | 'my-operation' | 'legacy-nodes'>>;
}) => {
  const track = useTracking<StakeTrackEvents>();

  const [addNodeModalActive, setAddNodeModalActive] = useState(false);

  const { auction, isLoading: isAuctionLoading } = useAuction();

  const { data: validatorsData, isLoading: isValidatorDataLoading } = useGqlQuery(
    getApyValidators,
    {
      refetchInterval: TABLE_POLL_INTERVAL,
      context: { clientName: 'statechainCache' },
    },
  );
  const validators = validatorsData?.validators?.nodes ?? [];

  const validatorApy = auction
    ? calculateValidatorApy({
        validators,
        authoritySetMaxCapacity: auction.targetSetSize,
        minValidatorStake: auction.minBid,
        blockReward: auction.blockReward,
      })
    : 0;

  return (
    <>
      <BannerCard
        header="Set up a Validator"
        body={<APYText value={validatorApy} suffix />}
        isBodyLoading={isValidatorDataLoading || isAuctionLoading}
        action={
          <Button
            type="secondary-standard"
            size="small"
            className="border-cf-gray-5 bg-cf-gray-4 px-2 py-3 hover:enabled:text-cf-white"
            onClick={() => {
              if (idSs58) {
                setSelectedTab('my-operation');
              } else {
                track(StakeEvents.AddNode, {
                  props: {
                    path: window.location.pathname,
                    position: 'table_top_right',
                  },
                });
                setAddNodeModalActive(true);
              }
            }}
          >
            Get Started
          </Button>
        }
        bannerImg={
          <Dots className="h-full w-full translate-x-[25px] translate-y-[5px] transform" />
        }
        headerClassName="text-white"
      />
      <RegisterNodeModal open={addNodeModalActive} onClose={() => setAddNodeModalActive(false)} />
    </>
  );
};
